import React, { useState, useEffect } from "react";
import { motion, useScroll, useTransform, useInView } from "motion/react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { AppIcon } from "../components/AppIcon";
import type { IconName } from "../assets/icons";
import {
  Clock,
  CreditCard,
  DollarSign,
  Package,
  Truck,
  ArrowRight,
  Building2,
  TrendingUp,
  Zap,
  Camera,
  ShoppingCart,
  Check,
  AlertTriangle,
  Star,
  Settings,
  Smartphone,
  UserCheck,
  Search,
  Bell,
  Store,
  CreditCard as PaymentIcon,
  Link2,
  BarChart3,
  Filter,
  Handshake,
  ChevronRight,
  Plus,
  ShieldCheck,
  Lock,
  RefreshCw,
  Home,
} from "lucide-react";

const CounterAnimation = ({ value, duration = 2000 }: { value: number; duration?: number }) => {
  const [count, setCount] = useState(0);
  const ref = React.useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (isInView) {
      let startTime: number;
      const animate = (timestamp: number) => {
        if (!startTime) startTime = timestamp;
        const progress = Math.min((timestamp - startTime) / duration, 1);
        setCount(Math.floor(progress * value));
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };
      requestAnimationFrame(animate);
    }
  }, [isInView, value, duration]);

  return <span ref={ref}>{count}</span>;
};

// Floating Icon Component with proper positioning
const FloatingIcon = ({
  icon: Icon,
  index,
  total,
}: {
  icon: React.ComponentType<any>;
  index: number;
  total: number;
}) => {
  const [positions, setPositions] = useState({ startX: 0, startY: 0, endX: 0, endY: 0 });
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    const updatePositions = () => {
      const viewWidth = window.innerWidth;

      // Generate random positions across the entire width and height of the hero section
      // Start from random positions spread across the banner
      const startX = Math.random() * viewWidth;
      const startY = Math.random() * 600 + 50; // Spread across hero height (roughly 600-800px)

      // End at different random positions for varied movement
      const endX = Math.random() * viewWidth;
      const endY = Math.random() * 600 + 50;

      setPositions({ startX, startY, endX, endY });
    };

    updatePositions();
    window.addEventListener("resize", updatePositions);
    return () => window.removeEventListener("resize", updatePositions);
  }, [index, total]);

  if (!mounted) return null;

  return (
    <motion.div
      className="absolute"
      initial={{
        opacity: 0,
        x: positions.startX,
        y: positions.startY,
      }}
      animate={{
        opacity: [0, 0.6, 0.4, 0.8, 0],
        x: positions.endX,
        y: positions.endY,
        rotate: [0, 360],
      }}
      transition={{
        duration: 15 + index * 1.5,
        repeat: Infinity,
        delay: index * 2,
        ease: "linear",
      }}
    >
      <Icon
        className={`w-${index % 3 === 0 ? "8" : index % 3 === 1 ? "6" : "7"} h-${index % 3 === 0 ? "8" : index % 3 === 1 ? "6" : "7"} ${index % 5 === 0
          ? "text-blue-400"
          : index % 5 === 1
            ? "text-purple-400"
            : index % 5 === 2
              ? "text-green-400"
              : index % 5 === 3
                ? "text-orange-400"
                : "text-red-400"
          }`}
      />
    </motion.div>
  );
};

// Floating Shape Component
const FloatingShape = ({ index, total }: { index: number; total: number }) => {
  const [positions, setPositions] = useState({ startX: 0, startY: 0, endX: 0, endY: 0 });
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    const updatePositions = () => {
      const viewWidth = window.innerWidth;

      // Generate completely random positions across the entire hero area
      const startX = Math.random() * viewWidth;
      const startY = Math.random() * 600 + 100; // Spread vertically across hero
      const endX = Math.random() * viewWidth;
      const endY = Math.random() * 600 + 100;

      setPositions({ startX, startY, endX, endY });
    };

    updatePositions();
    window.addEventListener("resize", updatePositions);
    return () => window.removeEventListener("resize", updatePositions);
  }, [index, total]);

  if (!mounted) return null;

  return (
    <motion.div
      className={`absolute ${index % 3 === 0
        ? "w-20 h-20 border-2 border-blue-300"
        : index % 3 === 1
          ? "w-16 h-16 border-2 border-purple-300"
          : "w-12 h-12 border-2 border-orange-300"
        } ${index % 2 === 0 ? "rounded-full" : "rounded-lg rotate-45"}`}
      style={index % 4 === 3 ? { borderColor: "#f58b01" } : {}}
      initial={{
        opacity: 0,
        x: positions.startX,
        y: positions.startY,
        scale: 0,
      }}
      animate={{
        opacity: [0, 0.2, 0.1, 0.3, 0],
        x: positions.endX,
        y: positions.endY,
        scale: [0, 1, 1.2, 0.8, 0],
        rotate: [0, 180, 360],
      }}
      transition={{
        duration: 20 + index * 2.5,
        repeat: Infinity,
        delay: index * 3,
        ease: "easeInOut",
      }}
    />
  );
};

// Payment Icon Component
const FloatingPayment = ({ index, total }: { index: number; total: number }) => {
  const [positions, setPositions] = useState({ startX: 0, startY: 0, endX: 0, endY: 0 });
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    const updatePositions = () => {
      const viewWidth = window.innerWidth;

      // Generate random positions across the entire hero area
      const startX = Math.random() * viewWidth;
      const startY = Math.random() * 600 + 100;
      const endX = Math.random() * viewWidth;
      const endY = Math.random() * 600 + 100;

      setPositions({ startX, startY, endX, endY });
    };

    updatePositions();
    window.addEventListener("resize", updatePositions);
    return () => window.removeEventListener("resize", updatePositions);
  }, [index, total]);

  if (!mounted) return null;

  return (
    <motion.div
      className="absolute"
      initial={{
        opacity: 0,
        x: positions.startX,
        y: positions.startY,
      }}
      animate={{
        opacity: [0, 0.4, 0.2, 0.6, 0],
        x: positions.endX,
        y: positions.endY,
        rotate: [0, 10, -10, 5, 0],
      }}
      transition={{
        duration: 30 + index * 5,
        repeat: Infinity,
        delay: index * 8,
        ease: "linear",
      }}
    >
      <DollarSign className="w-12 h-12 text-green-300" />
    </motion.div>
  );
};

const VendorMarketplaceMockup = () => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
      className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden"
    >
      <div className="bg-[#f5f5f7] px-5 py-3.5 border-b border-gray-200/80">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3.5 min-w-0">
            <div className="flex items-center gap-2 flex-shrink-0">
              <div className="w-3 h-3 rounded-full bg-[#ff5f57]"></div>
              <div className="w-3 h-3 rounded-full bg-[#febc2e]"></div>
              <div className="w-3 h-3 rounded-full bg-[#28c840]"></div>
            </div>
            <h3 className="font-medium text-gray-700 text-sm md:text-base truncate">
              RealEsy Vendor Marketplace
            </h3>
          </div>
          <div className="flex items-center gap-4 flex-shrink-0 text-gray-500">
            <Search className="w-4 h-4" />
            <Bell className="w-4 h-4" />
            <div className="w-7 h-7 rounded-full bg-gray-300"></div>
          </div>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <div className="w-64 bg-gray-50 border-r border-gray-200 p-4">
          <div className="space-y-3">
            <div className="flex items-center gap-3 p-2 bg-blue-100 rounded-lg">
              <Store className="w-5 h-5 text-blue-600" />
              <span className="text-sm font-medium">All Vendors</span>
            </div>
            <div className="flex items-center gap-3 p-2 hover:bg-gray-100 rounded-lg cursor-pointer">
              <Camera className="w-5 h-5 text-gray-400" />
              <span className="text-sm">Photography</span>
            </div>
            <div className="flex items-center gap-3 p-2 hover:bg-gray-100 rounded-lg cursor-pointer">
              <Package className="w-5 h-5 text-gray-400" />
              <span className="text-sm">Signage</span>
            </div>
            <div className="flex items-center gap-3 p-2 hover:bg-gray-100 rounded-lg cursor-pointer">
              <Lock className="w-5 h-5 text-gray-400" />
              <span className="text-sm">Lockboxes</span>
            </div>
            <div className="flex items-center gap-3 p-2 hover:bg-gray-100 rounded-lg cursor-pointer">
              <Home className="w-5 h-5 text-gray-400" />
              <span className="text-sm">Staging</span>
            </div>
            <div className="flex items-center gap-3 p-2 hover:bg-gray-100 rounded-lg cursor-pointer">
              <Settings className="w-5 h-5 text-gray-400" />
              <span className="text-sm">Inspections</span>
            </div>
          </div>

          <div className="mt-6">
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
              Quick Filters
            </h4>
            <div className="space-y-2">
              <div className="flex items-center gap-3 p-2 hover:bg-gray-100 rounded-lg cursor-pointer">
                <ShieldCheck className="w-4 h-4 text-green-500" />
                <span className="text-sm">Verified</span>
              </div>
              <div className="flex items-center gap-3 p-2 hover:bg-gray-100 rounded-lg cursor-pointer">
                <Star className="w-4 h-4 text-yellow-500" />
                <span className="text-sm">Top Rated</span>
              </div>
              <div className="flex items-center gap-3 p-2 hover:bg-gray-100 rounded-lg cursor-pointer">
                <DollarSign className="w-4 h-4 text-green-500" />
                <span className="text-sm">Best Value</span>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-bold">Vendor Marketplace</h2>
              <Badge className="bg-green-100 text-green-800">
                <CounterAnimation value={127} /> Active Vendors
              </Badge>
            </div>
            <div className="flex items-center gap-3">
              <Button size="sm" variant="outline">
                <Filter className="w-4 h-4 mr-2" />
                Filter
              </Button>
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" />
                Add Vendor
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {[
              {
                name: "Premium Photography Co.",
                type: "Photography",
                rating: 4.9,
                orders: 234,
                price: "$150/session",
                verified: true,
                badges: ["Fast Delivery", "Top Rated"],
                color: "border-green-200 bg-green-50",
              },
              {
                name: "Elite Signage Solutions",
                type: "Signage & Posts",
                rating: 4.8,
                orders: 189,
                price: "$35/sign",
                verified: true,
                badges: ["Bulk Discount", "Same Day"],
                color: "border-blue-200 bg-blue-50",
              },
              {
                name: "SecureLock Pro",
                type: "Lockboxes",
                rating: 4.7,
                orders: 156,
                price: "$25/unit",
                verified: true,
                badges: ["Warranty", "Installation"],
                color: "border-purple-200 bg-purple-50",
              },
              {
                name: "Staging Masters LLC",
                type: "Home Staging",
                rating: 4.9,
                orders: 78,
                price: "$2,500/room",
                verified: true,
                badges: ["Full Service", "Designer"],
                color: "border-orange-200 bg-orange-50",
              },
            ].map((vendor, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + index * 0.1 }}
                className={`p-4 rounded-lg border-2 cursor-pointer hover:shadow-lg transition-all duration-200 ${vendor.color}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium text-gray-900">{vendor.name}</h4>
                    {vendor.verified && <ShieldCheck className="w-4 h-4 text-green-600" />}
                  </div>
                  <div className="flex items-center gap-1">
                    <Star className="w-4 h-4 text-yellow-500 fill-current" />
                    <span className="text-sm font-medium">{vendor.rating}</span>
                  </div>
                </div>

                <p className="text-sm text-gray-600 mb-2">{vendor.type}</p>

                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-gray-500">{vendor.orders} orders</span>
                  <span className="font-bold" style={{ color: "#f58b01" }}>
                    {vendor.price}
                  </span>
                </div>

                <div className="flex flex-wrap gap-1 mb-3">
                  {vendor.badges.map((badge, badgeIndex) => (
                    <Badge key={badgeIndex} variant="secondary" className="text-xs">
                      {badge}
                    </Badge>
                  ))}
                </div>

                <Button size="sm" className="w-full bg-blue-600 hover:bg-blue-700">
                  View Details
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </motion.div>
            ))}
          </div>

          <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center gap-3 mb-2">
              <ShoppingCart className="w-5 h-5 text-blue-600" />
              <span className="font-medium text-blue-900">Current Order: 3 items</span>
            </div>
            <p className="text-sm text-blue-700">
              Total: $425 • Estimated delivery: 2-3 business days
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const FeatureSection = ({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <motion.section
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.8 }}
      className={`py-16 md:py-24 ${className}`}
    >
      {children}
    </motion.section>
  );
};

export default function VendorPortalLandingPage() {
  const { scrollYProgress } = useScroll();
  const backgroundY = useTransform(scrollYProgress, [0, 1], ["0%", "50%"]);

  const keyFeatures: {
    icon: IconName;
    title: string;
    description: string;
  }[] = [
      {
        icon: "vendorMarketplaceOneStopVendorMarketplace",
        title: "One-Stop Vendor Marketplace",
        description:
          "No more juggling multiple websites, emails, or phone calls. The Vendor Portal offers a centralized vendor catalog for real estate services — from signage providers to lockbox suppliers, photography, inspections, staging, and more.",
      },
      {
        icon: "vendorMarketplaceSmartInventorySync",
        title: "Smart Inventory Sync",
        description:
          "Every purchase made through the Vendor Portal automatically links to your inventory. Buy signage, lockboxes, or marketing materials, and they'll immediately appear in your brokerage or agent inventory.",
      },
      {
        icon: "vendorMarketplaceSeamlessOrdersPayments",
        title: "Seamless Ordering & Payments",
        description:
          "RealEsy makes transactions feel like modern e-commerce. Add products directly to cart, checkout securely, and track order progress in real time with instant notifications.",
      },
      {
        icon: "vendorMarketplaceTransparentVendorProfile",
        title: "Transparent Vendor Profiles",
        description:
          "Each vendor maintains their own profile with certifications, specialties, and customer reviews — so you can make informed choices with confidence. RealEsy provides the tools for transparency, while vendors showcase their credibility directly on the platform.",
      },
      {
        icon: "vendorMarketplaceFlexiblePartnership",
        title: "Flexible Partnerships",
        description:
          "Link vendors with a click, and their services sync instantly into your system. Need to change providers? Unlink just as easily — the system updates without disruption.",
      },
      {
        icon: "vendorMarketplaceOfficeControlledAgentFriendlyOrdering",
        title: "Office-Controlled & Agent-Friendly Ordering",
        description:
          "Brokerage offices can place orders on behalf of agents, ensuring consistency and preferred pricing. Individual agents can create orders independently when needed.",
      },
      {
        icon: "vendorMarketplaceSharedPaymentFlexibility",
        title: "Shared Payment Flexibility",
        description:
          "When an office generates an order, agents can log in and pay their portion directly. Payments can flow from the brokerage, office, or agent — flexibility built into every transaction.",
      },
      {
        icon: "vendorMarketplaceVendorAssisted",
        title: "Vendor-Assisted Ordering",
        description:
          "Vendors can also create orders on behalf of brokerages and agents. This reduces back-and-forth communication, prevents errors, and speeds up onboarding of services.",
      },
      {
        icon: "vendorMarketplaceInvoiceMonthlySpendReports",
        title: "Invoices & Monthly Spend Reports",
        description:
          "Stay on top of your brokerage finances with detailed invoices and automated spend reports. Track total purchases, payments, vendor-wise breakdowns, and office vs agent summaries.",
      },
      {
        icon: "vendorMarketplaceEverythingInTheMobileApp",
        title: "Everything in the Mobile App",
        description:
          "RealEsy Vendor Portal is fully available in the mobile app. Browse vendors, place orders, checkout securely, and view spend reports from your mobile dashboard.",
      },
    ];

  const workflows: {
    icon: IconName;
    title: string;
    description: string;
    steps: string[];
  }[] = [
      {
        title: "Browse & Compare Vendors",
        description: "Explore verified vendors by category, read reviews, and compare pricing.",
        icon: "vendorMarketplaceBrowseCompareVendor",
        steps: ["Browse categories", "Filter by ratings", "Compare prices", "Read reviews"],
      },
      {
        title: "Place Orders",
        description: "Add items to cart, checkout securely, and track delivery in real-time.",
        icon: "vendorMarketplacePlaceOrder",
        steps: ["Add to cart", "Secure checkout", "Choose payment", "Track delivery"],
      },
      {
        title: "Manage Payments",
        description: "Flexible payment options with transparent billing and automated reporting.",
        icon: "vendorMarketplaceManagePayment",
        steps: ["Multiple payment methods", "Split payments", "Auto billing", "Generate reports"],
      },
      {
        title: "Track & Report",
        description: "Monitor spending, track vendor performance, and generate detailed reports.",
        icon: "vendorMarketplaceTrackReport",
        steps: ["Spend tracking", "Vendor analytics", "Performance metrics", "Monthly reports"],
      },
    ];

  const faqs = [
    {
      q: "How do I add new vendors to the marketplace?",
      a: "You can request new vendors through the platform, and our team will verify and onboard qualified service providers that meet our quality standards.",
    },
    {
      q: "Can I see vendor performance analytics?",
      a: "Yes, you can view detailed analytics including delivery times, quality ratings, order history, and customer feedback for all vendors.",
    },
    {
      q: "What payment methods are accepted?",
      a: "We accept credit cards, bank transfers, and offer Net 30 terms for qualified brokerages. Payments can be split between offices and agents.",
    },
    {
      q: "How does inventory sync work?",
      a: "When you purchase items through the Vendor Portal, they automatically appear in your RealEsy inventory system, ready for installation and removal orders.",
    },
    {
      q: "Can vendors create orders for us?",
      a: "Yes, trusted vendors can create orders on your behalf to streamline the ordering process and reduce communication overhead.",
    },
    {
      q: "Is there a mobile app?",
      a: "Yes, the full Vendor Portal functionality is available in our mobile app, allowing you to browse, order, and track from anywhere.",
    },
    {
      q: "How are vendors verified?",
      a: "All vendors go through a comprehensive verification process including background checks, license verification, insurance confirmation, and quality assessments.",
    },
    {
      q: "Can I set spending limits?",
      a: "Yes, brokerages can set spending limits for agents and offices, with approval workflows for orders exceeding specified amounts.",
    },
  ];

  return (
    <>
      {/* Enhanced Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 via-white to-blue-50 py-20 md:py-32 overflow-hidden">
        {/* Animated Gradient Background */}
        <motion.div
          className="absolute inset-0"
          animate={{
            background: [
              "radial-gradient(600px circle at 20% 30%, rgba(59, 130, 246, 0.15), transparent 50%), radial-gradient(800px circle at 80% 70%, rgba(37, 99, 235, 0.15), transparent 50%), radial-gradient(400px circle at 40% 80%, rgba(245, 139, 1, 0.1), transparent 50%)",
              "radial-gradient(600px circle at 80% 20%, rgba(37, 99, 235, 0.15), transparent 50%), radial-gradient(800px circle at 20% 80%, rgba(245, 139, 1, 0.15), transparent 50%), radial-gradient(400px circle at 60% 40%, rgba(59, 130, 246, 0.1), transparent 50%)",
              "radial-gradient(600px circle at 20% 30%, rgba(59, 130, 246, 0.15), transparent 50%), radial-gradient(800px circle at 80% 70%, rgba(37, 99, 235, 0.15), transparent 50%), radial-gradient(400px circle at 40% 80%, rgba(245, 139, 1, 0.1), transparent 50%)",
            ],
          }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        />

        {/* Floating E-commerce Icons */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[
            Store,
            ShoppingCart,
            Package,
            CreditCard,
            Truck,
            Store,
            ShoppingCart,
            Package,
            CreditCard,
            Truck,
            Store,
            ShoppingCart,
          ].map((Icon, i) => (
            <FloatingIcon key={i} icon={Icon} index={i} total={12} />
          ))}
        </div>

        {/* Floating Shopping Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(8)].map((_, i) => (
            <FloatingShape key={`shopping-${i}`} index={i} total={8} />
          ))}
        </div>

        {/* Payment Flow Animation */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(5)].map((_, i) => (
            <FloatingPayment key={`payment-${i}`} index={i} total={5} />
          ))}
        </div>

        <div className="container mx-auto px-6 relative">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <div className="max-w-6xl mx-auto space-y-8">
                <div className="space-y-4">
                  <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-blue-800 leading-tight">
                    RealEsy Vendor Marketplace & Sales Orders
                  </h1>
                </div>

                <div className="max-w-4xl mx-auto space-y-6">
                  <p className="text-lg md:text-xl lg:text-2xl text-gray-700 leading-relaxed font-medium">
                    Your all-in-one platform to discover trusted real estate vendors, purchase
                    products, and manage every order seamlessly.
                  </p>
                  <p className="text-base md:text-lg text-gray-600 leading-relaxed">
                    RealEsy connects brokerages and agents directly with trusted vendors, making it
                    easy to browse products, place sales orders, track payments, and sync inventory
                    — all within one connected system.
                  </p>
                </div>

                {/* Offer Badge */}
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  className="inline-block mx-auto"
                >
                  <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 max-w-2xl mx-auto">
                    <div className="flex flex-col items-center gap-3">
                      <Badge className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 text-sm font-bold">
                        Exclusive Partner Offer
                      </Badge>
                      <p className="text-gray-900 text-center">
                        Get <span className="font-bold text-green-600">5% off</span> all sign orders
                        from Farmer Signs when you purchase through RealEsy.
                      </p>
                    </div>
                  </div>
                </motion.div>

                <div className="flex justify-center items-center pt-8">
                  <Button
                    size="lg"
                    className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-8 sm:px-12 py-5 sm:py-6 text-base sm:text-lg font-bold rounded-xl shadow-2xl hover:shadow-3xl transform hover:scale-[1.02] transition-all duration-300 w-full sm:w-auto border-0"
                  >
                    Explore Vendors
                    <ArrowRight className="ml-3 w-5 h-5" />
                  </Button>
                </div>
              </div>
            </motion.div>

            <div className="max-w-6xl mx-auto">
              <VendorMarketplaceMockup />
            </div>
          </div>
        </div>
      </section>

      {/* Problem, Solution, Result Section */}
      <FeatureSection className="bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-6">
                The Challenge of Managing Real Estate Vendors
              </h2>
              <p className="text-lg md:text-xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
                Managing vendors in real estate can be time-consuming and fragmented. Brokerages and
                agents juggle multiple vendor portals, emails, and phone calls just to place simple
                orders — leading to wasted time, errors, and unnecessary costs.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="p-8 rounded-2xl border-2 shadow-md"
              >
                <div className="w-12 h-12 bg-red-500 rounded-xl flex items-center justify-center mb-6">
                  <AlertTriangle className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">The Problem</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span>Multiple websites, emails, and calls for every service.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span>Inventory updates missed or delayed between vendors.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span>Confusing invoices and double payments across offices.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span>No consolidated view of total spend or vendor performance.</span>
                  </li>
                </ul>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="p-8 rounded-2xl border-2 shadow-md"
              >
                <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center mb-6">
                  <Zap className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">The Solution</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span>Centralized vendor marketplace with trusted partners.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span>Smart inventory sync that keeps products and usage up to date.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span>
                      Seamless payments and flexible billing options for offices or agents.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span>Real-time spend tracking and performance insights across vendors.</span>
                  </li>
                </ul>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="p-8 rounded-2xl border-2 shadow-md"
              >
                <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center mb-6">
                  <TrendingUp className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">The Result</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <Star className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Managers and agents spend less time coordinating with vendors.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Star className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Brokerages gain full visibility into orders, spend, and inventory.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Star className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Fewer billing errors, no duplicate payments, and faster approvals.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Star className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Spend less time on operations — and more time closing deals.</span>
                  </li>
                </ul>
              </motion.div>
            </div>
          </div>
        </div>
      </FeatureSection>

      {/* How RealEsy Vendor Portal Empowers Brokerages & Agents */}
      <FeatureSection className="bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-6">
                How RealEsy Vendor Portal Empowers Brokerages & Agents
              </h2>
              <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto">
                A comprehensive platform designed specifically for real estate vendor management and
                ordering workflows.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
              {keyFeatures.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -5, boxShadow: "0 20px 40px rgba(0,0,0,0.1)" }}
                  className="bg-white p-8 rounded-2xl border border-gray-200 group cursor-pointer"
                >
                  <div className="w-16 h-16 bg-white rounded-2xl shadow-md border border-gray-200 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <AppIcon name={feature.icon} size={36} alt={feature.title} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </FeatureSection>

      {/* Vendor Marketplace Workflow */}
      <FeatureSection className="bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-6">
                Streamlined Vendor Workflow
              </h2>
              <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto">
                From discovery to payment, manage your entire vendor relationship lifecycle in one
                platform.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {workflows.map((workflow, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -5, boxShadow: "0 20px 40px rgba(0,0,0,0.1)" }}
                  className="bg-gray-50 p-8 rounded-2xl border border-gray-200 group cursor-pointer"
                >
                  <div className="w-14 h-14 bg-white rounded-2xl shadow-md border border-gray-200 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <AppIcon name={workflow.icon} size={32} alt={workflow.title} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{workflow.title}</h3>
                  <p className="text-gray-600 leading-relaxed mb-6">{workflow.description}</p>

                  <div className="space-y-2">
                    {workflow.steps.map((step, stepIndex) => (
                      <div
                        key={stepIndex}
                        className="flex items-center gap-2 text-sm text-gray-600"
                      >
                        <ChevronRight className="w-3 h-3" />
                        <span>{step}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </FeatureSection>

      {/* Benefits Section */}
      <FeatureSection className="bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-6">
                Why Brokerages Choose RealEsy Vendor Portal
              </h2>
              <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto">
                Built specifically for real estate teams — connect with trusted vendors, manage
                purchases, and track spending effortlessly.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
              {(
                [
                  {
                    icon: "vendorMarketplaceSimplifyVendorManagement",
                    title: "Simplify Vendor Management",
                    description:
                      "Access all your trusted vendors in one marketplace — no more juggling multiple websites or email threads.",
                  },
                  {
                    icon: "vendorMarketplaceControlCost",
                    title: "Control Costs",
                    description:
                      "Transparent pricing, built-in discounts, and spending visibility keep your brokerage budget in check.",
                  },
                  {
                    icon: "vendorMarketplaceGainVisibility",
                    title: "Gain Visibility",
                    description:
                      "Track every order, payment, and product across offices and agents — all in one dashboard.",
                  },
                  {
                    icon: "vendorMarketplaceEmpowerOfficeAgent",
                    title: "Empower Offices & Agents",
                    description:
                      "Let offices order on behalf of agents or allow agents to purchase directly — with flexible billing and approvals.",
                  },
                  {
                    icon: "vendorMarketplaceSellMoreManageLess",
                    title: "Sell More, Manage Less",
                    description:
                      "Cut admin work and vendor follow-ups so your team can focus on listings, clients, and closings.",
                  },
                ] as { icon: IconName; title: string; description: string }[]
              ).map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 + index * 0.1 }}
                  className="bg-white p-6 rounded-2xl border border-gray-200 text-center group cursor-pointer"
                >
                  <div className="w-14 h-14 bg-white rounded-2xl shadow-md border border-gray-200 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                    <AppIcon name={benefit.icon} size={32} alt={benefit.title} />
                  </div>
                  <h3 className="font-bold text-lg mb-2">{benefit.title}</h3>
                  <p className="text-gray-600 text-sm">{benefit.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </FeatureSection>

      {/* Bring Your Favorite Vendors Section */}
      <section className="bg-gradient-to-br from-orange-50 to-amber-50 py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-6">
                Bring Your Favorite Vendors to RealEsy
              </h2>
              <p className="text-lg md:text-xl text-gray-700 mb-10 leading-relaxed max-w-3xl mx-auto">
                Already working with a trusted vendor? Let us know, and we'll invite them to join
                the RealEsy Marketplace so you can manage everything in one place.
              </p>
              <Button
                size="lg"
                className="bg-[#f58b01] hover:bg-[#e07d00] text-white px-10 py-6 text-lg font-bold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-300"
              >
                Request Vendor
                <ArrowRight className="ml-3 w-5 h-5" />
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FeatureSection className="bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-6">
                Frequently Asked Questions
              </h2>
              <p className="text-lg md:text-xl text-gray-700">
                Get answers to common questions about our vendor portal and marketplace system.
              </p>
            </div>

            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white p-8 rounded-2xl border border-gray-200"
                >
                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0 mt-1">
                      <AppIcon name="faqQuestion" size={28} alt="FAQ" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-lg text-gray-900 mb-3">{faq.q}</h3>
                      <p className="text-gray-700 leading-relaxed">{faq.a}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </FeatureSection>

      {/* Enhanced Final CTA */}
      <section className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 py-24 overflow-hidden">
        <motion.div style={{ y: backgroundY }} className="absolute inset-0 opacity-20">
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0 }}
              animate={{
                opacity: [0, 1, 0],
                scale: [0, 1, 0],
                x: Math.random() * 800,
                y: Math.random() * 600,
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                delay: i * 0.8,
                ease: "easeInOut",
              }}
              className="absolute w-8 h-8"
            >
              {i % 3 === 0 ? (
                <Store className="w-full h-full text-white" />
              ) : i % 3 === 1 ? (
                <ShoppingCart className="w-full h-full text-white" />
              ) : (
                <div className="w-full h-full bg-white rounded-full opacity-60"></div>
              )}
            </motion.div>
          ))}
        </motion.div>

        <div className="container mx-auto px-6 relative">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight">
              Simplify your vendor management.
            </h2>
            <p className="text-lg md:text-xl text-blue-100 mb-12 leading-relaxed">
              Explore our marketplace and see how RealEsy connects your offices, agents, and vendors
              effortlessly.
            </p>

            <div className="flex justify-center items-center">
              <Button
                size="lg"
                className="bg-white text-blue-700 hover:bg-blue-50 hover:text-blue-800 px-8 sm:px-12 py-5 sm:py-6 text-base sm:text-lg font-bold rounded-xl shadow-2xl hover:shadow-3xl transform hover:scale-[1.02] transition-all duration-300 border-2 border-white w-full sm:w-auto"
              >
                Explore Vendors
                <ArrowRight className="ml-3 w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
